import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { DetailPieModal } from './DetailPieModal';

const COLORS = [
  '#fc9133',
  '#1d1d1b',
  '#10d8b4',
  '#3498db',
  '#9b59b6',
  '#ff6b6b',
  '#f7c325',
  '#2ecc71',
  '#5c6bc0',
  '#e67e22',
];

const PieChartWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  h3 {
    margin-bottom: 2px;
  }

  .pie-tooltip {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .pie-legend {
    list-style: none;
    width: 100%;
    margin: 4px 0 8px;
    padding: 0 12px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    box-sizing: border-box;
  }

  .pie-legend li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    line-height: 1.3;
    color: #434343;
    margin: 0;
    cursor: pointer;
  }

  .pie-legend-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .pie-legend-label {
    flex: 1;
    min-width: 0;
  }

  .pie-legend-value {
    color: #6b6b6b;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const DefaultTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const item = payload[0].payload;
  return (
    <div className="pie-tooltip">
      <strong>{item.name}</strong>
      <div>
        {item.value}
        {item.percentage != null ? ` (${item.percentage}%)` : ''}
      </div>
    </div>
  );
};

const PieChartComponent = ({
  title,
  subtitle,
  data,
  width = 280,
  height = 210,
  outerRadius = 78,
  dataKey = 'value',
  nameKey = 'name',
  customTooltip = null,
  pieType,
}) => {
  const [list, setList] = useState([]);
  const [category, setCategory] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [count, setCount] = useState(0);

  const { chartData, detailsByName } = useMemo(() => {
    const slim = [];
    const details = {};
    (data || []).forEach((item) => {
      const value = Number(item?.[dataKey]);
      if (!value) return;
      const name = item[nameKey];
      slim.push({
        name,
        value,
        percentage: item.percentage,
      });
      details[name] = item;
    });
    const total = slim.reduce((sum, item) => sum + item.value, 0);
    slim.forEach((item) => {
      item.percentage = total ? ((item.value / total) * 100).toFixed(1) : '0';
    });
    return { chartData: slim, detailsByName: details };
  }, [data, dataKey, nameKey]);

  const handlePieClick = (entry) => {
    const original = detailsByName[entry?.name] || entry;
    let extractedData = [];
    if (pieType === 'leader') {
      extractedData = (original.data || [])
        .map((leader) => ({
          name: leader.name,
          lastName: leader.lastName,
          phone: leader.phone,
          email: leader.email,
          identification: leader.identification,
          ocupation: leader.ocupation,
          age: calculateAge(leader.birthdate),
          leader: leader.GuestLeader || null,
          groupName: leader.groupName,
        }))
        .sort((a, b) => {
          if (a.name.trim() < b.name.trim()) return -1;
          if (a.name.trim() > b.name.trim()) return 1;
          return 0;
        });
    } else if (pieType === 'disciple') {
      extractedData = (original.data || [])
        .map((disciple) => ({
          name: disciple.female ? disciple.female?.name : disciple.male?.name,
          lastName: disciple.female ? disciple.female?.lastName : disciple.male?.lastName,
          phone: disciple.female ? disciple.female?.phone : disciple.male?.phone,
          email: disciple.female ? disciple.female?.email : disciple.male?.email,
          identification: disciple.female
            ? disciple.female?.identification
            : disciple.male?.identification,
          ocupation: disciple.female ? disciple.female?.ocupation : disciple.male?.ocupation,
          age: calculateAge(
            disciple.female ? disciple.female?.birthdate : disciple.male?.birthdate
          ),
        }))
        .sort((a, b) => {
          if (a.name.trim() < b.name.trim()) return -1;
          if (a.name.trim() > b.name.trim()) return 1;
          return 0;
        });
    } else if (pieType === 'group') {
      extractedData = (original.data || [])
        .map((group) => ({
          groupName: group.groupName,
          leaderName: group.femaleLeader
            ? group.femaleLeader?.name ?? ''
            : group.maleLeader?.name ?? '',
          discipleshipDay: getWeekday(group.dayOfWeek),
          discipleshipTime: group.time,
          category: group.activityName,
          modality:
            group.online && group.takeattendance
              ? 'Mixto'
              : group.online
              ? 'Virtual'
              : 'Presencial',
          leaderType:
            group.kind === 'Grupo_Discipulado' ? 'Equipo de discipulado' : 'Equipo de líderes',
          members: group.GroupMembers?.map((member) => ({
            name: member.female ? member.female?.name : member?.male?.name,
            lastName: member.female ? member.female?.lastName : member.male?.lastName,
            phone: member.female ? member.female?.phone : member.male?.phone,
            email: member.female ? member.female?.email : member.male?.email,
            identification: member.female
              ? member.female?.identification
              : member.male?.identification,
            ocupation: member.female ? member.female?.ocupation : member.male?.ocupation,
            age: calculateAge(member.female ? member.female?.birthdate : member.male?.birthdate),
          })).sort((a, b) => {
            if (a.name?.trim() < b.name?.trim()) return -1;
            if (a.name?.trim() > b.name?.trim()) return 1;
            return 0;
          }),
        }))
        .sort((a, b) => {
          if (a.groupName.trim() < b.groupName.trim()) return -1;
          if (a.groupName.trim() > b.groupName.trim()) return 1;
          return 0;
        });
    }
    setList(extractedData);
    setCategory(original.name || entry?.name || '');
    setCount(original.value ?? entry?.value);
    setIsModalOpen(true);
  };

  const getWeekday = (day) => {
    switch (day) {
      case 0:
        return 'Domingo';
      case 1:
        return 'Lunes';
      case 2:
        return 'Martes';
      case 3:
        return 'Miércoles';
      case 4:
        return 'Jueves';
      case 5:
        return 'Viernes';
      case 6:
        return 'Sábado';
      default:
        return 'No definido';
    }
  };

  const calculateAge = (birthdate) => {
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <PieChartWrap className="graphic pie-chart" style={{ cursor: pieType ? 'pointer' : 'default' }}>
      {title && <h3>{title}</h3>}
      {subtitle && <p className="pie-subtitle">{subtitle}</p>}
      {chartData.length > 0 ? (
        <>
          <PieChart width={width} height={height}>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={outerRadius}
              fill="#8884d8"
              label={false}
              isAnimationActive={false}
              onClick={(entry) => handlePieClick(entry)}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${entry.name || index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={customTooltip || <DefaultTooltip />} />
          </PieChart>
          <ul className="pie-legend">
            {chartData.map((item, index) => (
              <li key={item.name || index} onClick={() => handlePieClick(item)}>
                <span
                  className="pie-legend-swatch"
                  style={{ background: COLORS[index % COLORS.length] }}
                />
                <span className="pie-legend-label">{item.name}</span>
                <span className="pie-legend-value">
                  {item.percentage != null ? `${item.percentage}%` : item.value}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="chart-empty">No hay datos para mostrar</div>
      )}

      {isModalOpen && (
        <DetailPieModal
          pieType={pieType}
          title={title}
          category={category}
          list={list}
          open={isModalOpen}
          setOpen={setIsModalOpen}
          count={count}
        />
      )}
    </PieChartWrap>
  );
};

export default PieChartComponent;
